/**
 * EQuIP APC Dashboard Wrapper Page
 *
 * Wrapper page for the supervising physician APC performance view.
 * Lives in pages/ (not views/equip/) so it is NOT overwritten by update.sh.
 *
 * Adds the shared site chrome — FloatingNav, HeroSection, max-w-7xl container —
 * then renders EquipAPCDashboardPage from the synced views/equip/ directory.
 *
 * Follows the established non-synced wrapper pattern from EquipDashboardPage
 * and EquipLeadershipWrapper. Nav items will be refined in Plan 02 to show
 * the full physician navigation set.
 *
 * Route: /apc-dashboard (protected by EQUIP_PERFORMANCE_ROLES)
 */

import { useRBAC } from '@/hooks/use-rbac'
import { ROLES } from '@/lib/roles'
import { BarChart3, BookOpen, Users } from 'lucide-react'

import { FloatingNav } from '@/components/layout/FloatingNav'
import { HeroSection } from '@/components/layout/HeroSection'
import { useRosterProfile } from '@/hooks/equip/useRosterProfile'
import { EquipAPCDashboardPage } from '@/views/equip/EquipAPCDashboardPage'
import { useEquipScopeOptional } from '@/views/equip/context/EquipScopeContext'

export function EquipAPCDashboardWrapper() {
  const { isAdmin, hasRole } = useRBAC()
  const { data: rosterProfile, isLoading: isProfileLoading } = useRosterProfile()
  const scope = useEquipScopeOptional()

  // Detect equip.read-only users (no admin, no lead, no CSS)
  const navRolePrefix =
    isAdmin || hasRole(ROLES.EQUIP_PHYSICIAN_LEAD)
      ? 'physician'
      : hasRole(ROLES.EQUIP_APC_LEAD) || hasRole(ROLES.EQUIP_SENIOR_LEAD)
        ? 'non-physician'
        : undefined
  const isReadOnly = !isAdmin && !navRolePrefix && !hasRole(ROLES.EQUIP_CSS)

  // Hide page chrome until roster profile resolves for equip.read users (avoids nav flash)
  if (isReadOnly && isProfileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    )
  }

  // This page is the physician APC view — always show the 3-item physician nav
  const navItems = [
    { id: 'performance', label: 'My Performance', path: '/performance', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'framework', label: 'Framework', path: '/framework', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'apc-dashboard', label: 'My APCs', path: '/apc-dashboard', icon: <Users className="w-4 h-4" /> },
  ]

  // Dynamic hero based on admin vs physician self-view
  const getHeroTitle = (): string => {
    if (isAdmin) {
      // Admin impersonating a physician
      if (scope?.selectedPhysicianNpi) {
        const physician = scope.supervisingPhysicians.find(p => p.npi === scope.selectedPhysicianNpi)
        if (physician?.name) {
          const lastName = physician.name.split(',')[0].trim()
          return `Dr. ${lastName}`
        }
        return 'APC Performance'
      }
      // Admin, no physician selected yet
      return 'APC Performance'
    }
    // Non-admin physician self-viewing
    if (rosterProfile?.lastName) {
      return `Dr. ${rosterProfile.lastName}`
    }
    return 'APC Performance'
  }

  const getHeroDescription = (): string => {
    if (isAdmin && !scope?.selectedPhysicianNpi) {
      return 'Select a physician to view their supervised APCs.'
    }
    return 'Review quality measures and outcomes for the APCs you supervise.'
  }

  const heroTitle = getHeroTitle()
  const heroDescription = getHeroDescription()

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      <FloatingNav
        items={navItems}
        showHome={false}
      />

      <HeroSection
        role="physician"
        badgeText="APC Performance"
        title={heroTitle}
        description={heroDescription}
      />

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6">
          <EquipAPCDashboardPage />
        </div>
      </section>
    </div>
  )
}
